"use client";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, cloneElement, useCallback, useContext, useEffect, useId, useRef, useState, } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { CSSTransition } from "react-transition-group";
import { useDetectInsideInteraction, usePortal, useScrollLock, } from "@travel/shared/hooks";
import { IconButton } from "../../buttons";
import { Scrim } from "../../vfx";
import { OverlayRouterContext } from "./overlay.provider";
import CloseIcon from "@travel/shared/assets/icons/icon-close.svg";
import styles from "./overlay.module.css";
export function Overlay(props) {
    var _a = props.open, isOpenProp = _a === void 0 ? false : _a, trigger = props.trigger, content = props.content, timeout = props.timeout, _b = props.name, name = _b === void 0 ? "" : _b, _c = props.disableRouter, disableRouter = _c === void 0 ? false : _c, resetScrollOnOpened = props.resetScrollOnOpened, lockScrollOnOpened = props.lockScrollOnOpened, _d = props.scrollLock, scrollLock = _d === void 0 ? true : _d, onClickTrigger = props.onClickTrigger, onOpen = props.onOpen, onOpened = props.onOpened, onClose = props.onClose, onClosed = props.onClosed, onOpenChange = props.onOpenChange;
    var id = useId();
    var _e = __read(useContext(OverlayRouterContext), 2), router = _e[0], _f = _e[1], addOverlayRouter = _f.add, removeOverlayRouter = _f.remove, replace = _f.replace;
    var _g = __read(useState(isOpenProp), 2), isOpen = _g[0], setIsOpen = _g[1];
    var portal = usePortal("overlay");
    var _h = useScrollLock(), lockScroll = _h.lockScroll, unlockScroll = _h.unlockScroll, applyLock = _h.applyLock;
    var _j = __read(useContext(OverlayContext), 2), _k = __read(_j[0].added, 1), focused = _k[0], _l = _j[1], focus = _l.focus, add = _l.add, remove = _l.remove;
    var routeParam = router === null || router === void 0 ? void 0 : router[name];
    var routeParamValue = Array.isArray(routeParam) ? routeParam[0] : undefined;
    var isOpenInRouter = !!routeParam;
    useEffect(function () {
        setIsOpen(isOpenInRouter);
    }, [isOpenInRouter]);
    useEffect(function () {
        if (!isOpen && isOpenInRouter) {
            replace(name, false);
        }
    }, [isOpen]);
    useEffect(function () {
        return function () {
            remove(id);
        };
    }, [id]);
    useEffect(function () {
        if (!name || disableRouter)
            return;
        addOverlayRouter(name);
        return function () {
            removeOverlayRouter(name);
        };
    }, [name, disableRouter]);
    useEffect(function () {
        if (!scrollLock)
            return;
        return function () {
            unlockScroll();
        };
    }, [scrollLock]);
    useEffect(function () {
        setIsOpen(isOpenProp);
        if (scrollLock && isOpenProp && !lockScrollOnOpened) {
            lockScroll();
        }
    }, [scrollLock, isOpenProp, lockScrollOnOpened]);
    useEffect(function () {
        if (isOpen)
            add(id);
        else
            remove(id);
    }, [id, isOpen]);
    return (_jsxs(_Fragment, { children: [!!trigger &&
                cloneElement(trigger, {
                    onClick: function () {
                        onClickTrigger === null || onClickTrigger === void 0 ? void 0 : onClickTrigger();
                        open();
                    },
                    "aria-controls": id,
                }), portal
                ? createPortal(_jsx(OverlayWindow, __assign({}, props, { id: id, open: isOpen, close: close, timeout: timeout, focused: focused === id, onOpen: handleOpen, onOpened: handleOpened, onClose: handleClose, onClosed: handleClosed, children: typeof content === "function"
                        ? content({
                            open: open,
                            close: close,
                        }, routeParamValue)
                        : content })), portal)
                : null] }));
    function handleOpen() {
        if (lockScrollOnOpened) {
            setTimeout(lockScroll, 50);
        }
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
    }
    function handleOpened() {
        if (scrollLock && resetScrollOnOpened) {
            applyLock(0);
        }
        onOpened === null || onOpened === void 0 ? void 0 : onOpened();
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(true);
    }
    function handleClose() {
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }
    function handleClosed() {
        if (scrollLock) {
            unlockScroll();
        }
        remove(id);
        onClosed === null || onClosed === void 0 ? void 0 : onClosed();
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false);
    }
    function open() {
        if (scrollLock && !lockScrollOnOpened) {
            lockScroll();
        }
        setIsOpen(true);
    }
    function close() {
        setIsOpen(false);
    }
}
function OverlayWindow(_a) {
    var _b;
    var id = _a.id, className = _a.className, classes = _a.classNames, children = _a.children, _c = _a.open, open = _c === void 0 ? false : _c, close = _a.close, _d = _a.timeout, timeout = _d === void 0 ? 300 : _d, _e = _a.trap, trap = _e === void 0 ? true : _e, _f = _a.focused, focused = _f === void 0 ? true : _f, _g = _a.fill, fill = _g === void 0 ? false : _g, _h = _a.canClose, canClose = _h === void 0 ? true : _h, contentStyle = _a.contentStyle, onOpen = _a.onOpen, onOpened = _a.onOpened, onClose = _a.onClose, onClosed = _a.onClosed, _j = _a.noScrim, noScrim = _j === void 0 ? false : _j, _k = _a.hideClose, hideClose = _k === void 0 ? false : _k, _l = _a.scrollLock, scrollLock = _l === void 0 ? true : _l, _m = _a.unmountOnExit, unmountOnExit = _m === void 0 ? true : _m, _o = _a.closeIcon, closeIcon = _o === void 0 ? _jsx(CloseIcon, { width: 32, height: 32 }) : _o;
    var ref = useRef(null);
    var _p = __read(useState(open), 2), isOpen = _p[0], setIsOpen = _p[1];
    var _q = __read(useState(false), 2), isReady = _q[0], setIsReady = _q[1];
    var contentRef = useDetectInsideInteraction(function (isInside, event) {
        if (!isInside) {
            close === null || close === void 0 ? void 0 : close();
        }
    }, focused && open && !!canClose, ["mousedown"]);
    useEffect(function () {
        var isDestroyed = false;
        setTimeout(function () {
            if (isDestroyed)
                return;
            setIsReady(true);
        }, 10);
        return function () {
            isDestroyed = true;
        };
    }, []);
    var content = (_jsxs("div", { id: id, className: classNames(styles["overlay"], className, (_b = {},
            _b[styles["hide-close"]] = hideClose,
            _b[styles["is-open"]] = open || isOpen,
            _b[styles["will-unmount"]] = unmountOnExit,
            _b[styles["is-locked"]] = scrollLock,
            _b[styles["fill"]] = fill,
            _b)), ref: ref, role: "dialog", tabIndex: -1, children: [canClose && _jsx(IconButton, { className: styles["close"], icon: closeIcon }), _jsx("div", { className: classNames(styles["content"], classes === null || classes === void 0 ? void 0 : classes.content), style: contentStyle, ref: contentRef, children: _jsx("div", { className: classNames(styles["inner"], classes === null || classes === void 0 ? void 0 : classes.inner), children: children }) })] }));
    return (_jsxs("div", { className: styles["inner"], children: [_jsx(Scrim, { className: classes === null || classes === void 0 ? void 0 : classes.scrim, open: open, portal: false, onClick: !!canClose ? close : undefined }), _jsx(CSSTransition, { in: open, nodeRef: ref, timeout: timeout, appear: true, classNames: {
                    enter: classNames(classes === null || classes === void 0 ? void 0 : classes.enter, styles["enter"]),
                    enterActive: classNames(classes === null || classes === void 0 ? void 0 : classes.enterActive, styles["enter-active"]),
                    exit: classNames(classes === null || classes === void 0 ? void 0 : classes.exit, styles["exit"]),
                    exitActive: classNames(classes === null || classes === void 0 ? void 0 : classes.exitActive, styles["exit-active"]),
                }, onEnter: function () {
                    if (!isOpen)
                        setIsOpen(true);
                    onOpen === null || onOpen === void 0 ? void 0 : onOpen();
                }, onEntered: onOpened, onExit: onClose, onExited: function () {
                    setIsOpen(false);
                    onClosed === null || onClosed === void 0 ? void 0 : onClosed();
                }, unmountOnExit: unmountOnExit, children: content })] }));
}
var DEFAULT_OVERLAY_PROVIDER_STATE = {
    focused: null,
    added: [],
};
var DEFAULT_OVERLAY_PROVIDER_ACTIONS = {
    add: function () { },
    remove: function () { },
    focus: function () { },
};
var OverlayContext = createContext([DEFAULT_OVERLAY_PROVIDER_STATE, DEFAULT_OVERLAY_PROVIDER_ACTIONS]);
export function OverlayProvider(_a) {
    var children = _a.children;
    var _b = __read(useState(DEFAULT_OVERLAY_PROVIDER_STATE), 2), state = _b[0], setState = _b[1];
    var add = useCallback(function (id) {
        if (!id || state.added.indexOf(id) > -1)
            return;
        setState(__assign(__assign({}, state), { added: __spreadArray([id], __read(state.added), false) }));
    }, [state]);
    var remove = useCallback(function (id) {
        if (!id)
            return;
        var index = state.added.indexOf(id);
        if (index < 0)
            return;
        var added = __spreadArray([], __read(state.added), false);
        added.splice(index, 1);
        setState(__assign(__assign({}, state), { added: added }));
    }, [state]);
    var focus = useCallback(function (id) { }, [state]);
    return (_jsx(OverlayContext.Provider, { value: [state, { add: add, remove: remove, focus: focus }], children: children }));
}
